<script setup>
import {ref, reactive, computed, watch, onMounted, onBeforeUnmount} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useMainOrdersStore} from '@/stores/mainOrders.js'
import {useConfigStore} from '@/stores/config.js'
import {useUserStore} from "@/stores/user.js";
import {useCurrencyPricesStore} from "@/stores/currencyPrices.js";
import {numeric, required, maxLength, helpers} from '@vuelidate/validators';
import {useVuelidate} from '@vuelidate/core';
import Tooltip from "@/components/Tooltip/index.vue";
import Loading from "@/components/Icon/Loading.vue";
import {Icon} from "@iconify/vue";
import {showMessage} from "@/utils/showMessage.js";
import showAlert from "@/utils/showAlert.js";
import {TabGroup, TabList, Tab, TabPanels, TabPanel} from "@headlessui/vue";
import Attachments from "@/views/orders/Details/Attachments.vue";
import Transfer from "@/views/orders/Details/Transfer.vue";
import serversModal from "@/views/orders/Details/servers.vue"
import Swal from "sweetalert2";
import warning from "@/assets/images/icon/mingcute--warning-line.png";
import {Button, InputGroup, InputGroupAddon, InputText, Textarea} from "primevue";
import FloatLabel from "primevue/floatlabel";
import Select from "primevue/select";
import HotOrders from "@/views/orders/Details/CurrencyBuy/AddOrder/HotOrders.vue";
import AddHotOrder from "@/views/orders/Details/CurrencyBuy/AddOrder/AddHotOrder.vue";
import {truncateTo2} from "@/utils/truncateTo2.js";
import Users from "@/components/Users.vue";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {discordRegistrationTime} from "@/utils/discordRegistrationTime.js";

/* tabs */
const tabs = [
  {
    title: "Details",
  },
  {
    title: "Attachments",
  },
];
const selectedTab = ref(0)
const showTransfer = ref(false)
const plusTax = ref(false)

/* attachments */
const attachments = ref([])

/* Auxiliary keys allowed for all fields */
const helperKeys = [
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'Home', 'End'
]

/* ---------- Stores ---------- */
const route = useRoute()
const router = useRouter()
const ordersStore = useMainOrdersStore()
const configStore = useConfigStore()
const userStore = useUserStore();
const currencyPricesStore = useCurrencyPricesStore();

/* ---------- Loading status ---------- */
const fetching = ref(true)
const loading = ref(true)
const saving = ref(false)
const isUploadingAttachment = ref(false)

const selectedTaker = ref({})
const selectedPaymentMethod = ref({})
const currencyPrices = ref([])
const excludeCurrencyPrices = ["Toman Gold Buy Manual", "Toman Gold Buy Auto", "Toman Shop"];
const includeCurrencyPrices = ["Toman", "Instant Toman", "USDT"];
const tomanGoldCurrency = ref({})
const tomanJalaliCurrency = ref({})

/* ---------- The field that the user last edited. ---------- */
const activeField = ref(null) // 'price' | 'tomanPrice' | null

/* ---------- Order object ---------- */
const order = reactive({
  state: 'New',
  game: '',
  server: '',
  account: '',
  supplier: '',
  amount: '',
  price: '',
  value: '',
  usd_tmn: '',
  tomanPrice: '',
  tomanValue: '',
  notes: '',
  id: '',
  uuid: '',
  paymentMethod: '',
  takerID: '',
})

const rules = computed(() => {
  return {
    state: {required},
    game: {required},
    server: {required},
    account: {required},
    amount: {required, numeric},
    price: {required, numeric},
    value: {required},
    usd_tmn: {required, numeric},
    tomanPrice: {required, numeric},
    tomanValue: {required},
    paymentMethod: {required},
  };
});
const $vulidate = useVuelidate(rules, order);

/* ---------- Related to server ---------- */
const isShowServersModal = ref(false)

function openServersModal() {
  if (servers.value.length > 0) {
    isShowServersModal.value = true
  }
}

function closeServersModal() {
  isShowServersModal.value = false
}

function selectServer(server) {
  order.server = server
  closeServersModal()
}

/* ---------- Related to state ---------- */
const isStateOpen = ref(false)
const stateDropdownRef = ref(null)
const states = ['New', 'Delivering', 'Delivered', 'Canceled', 'Paid']
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Delivering: 'bg-[#FF9800]',
  Delivered: 'bg-[#2196F3]',
  Canceled: 'bg-[#F44336]',
  Paid: 'bg-[#4CAF50]',
}

const toggleStateDropdown = () => isStateOpen.value = !isStateOpen.value

const selectState = (state) => {
  order.state = state
  isStateOpen.value = false
}

const handleClickOutside = (e) => {
  if (stateDropdownRef.value && !stateDropdownRef.value.contains(event.target)) isStateOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

/* ---------- Select options ---------- */
const games = computed(() => configStore.getGamesByService('Currency') || [])

const selectedGame = computed(() => games.value.find(g => g.game === order.game) || null)

const servers = computed(() => {
  if (!selectedGame.value) return []

  const servers = selectedGame.value.servers

  if (!servers.includes('All')) {
    return servers
  }

  return [
    'All',
    ...servers.filter(server => server !== 'All')
  ]
})

const accounts = computed(() => {
  return order.game ? ordersStore.getGameAccountByGame(order.game) || [] : []
})

/* ---------- Selected account and its title ---------- */
const selectedAccountObj = computed(() => accounts.value.find(a => a.id === order.account) || null)

const accountTitle = computed(() => selectedAccountObj.value ? selectedAccountObj.value.title : '')

/* Digit only (no dots) */
function allowDigits(e) {
  if (
      helperKeys.includes(e.key) ||
      // Allow Ctrl+C / Ctrl+V / Ctrl+A and…:
      (e.ctrlKey || e.metaKey)
  ) return

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* Digit + a single dot  */
function allowDigitsDot(e) {
  if (
      helperKeys.includes(e.key) ||
      (e.ctrlKey || e.metaKey)
  ) return

  if (e.key === '.') {
    if (e.target.value.includes('.')) e.preventDefault()
    return
  }

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

/* ---------- Reaction to Game Change ---------- */
function onGameChange() {
  order.server =
      servers.value.length === 1 ? servers.value[0] : ''
  order.account =
      accounts.value.length === 1 ? accounts.value[0].id : ''
}

function trimDecimals(num, decimals) {
  return num.toFixed(decimals).replace(/\.?0+$/, '')
}

function formatNumber(val, maxDecimals = 0) {
  if (val === '' || val === null || val === undefined) return ''
  const num = Number(val.toString().replace(/,/g, ''))
  if (isNaN(num)) return ''
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  })
}


/* ---------- Inputs ---------- */
function onAmountInput(raw) {
  order.amount = sanitizeNumber(raw)
  recalc()
}

function onPriceInput(raw) {
  const cleaned = sanitizeNumber(raw)
  if (cleaned === '') {
    order.price = ''
    if (activeField.value === 'price') activeField.value = null
  } else {
    order.price = cleaned // no trimDecimals — save with all decimals
    activeField.value = 'price'
  }

  if (order.price && Number(order.usd_tmn)) {
    order.tomanPrice = (truncateTo2((Number(order.price) * Number(order.usd_tmn)))).toString()
  } else {
    order.tomanPrice = ''
  }

  recalc()
}

function onUsdInput(raw) {
  order.usd_tmn = sanitizeNumber(raw)  // Allowed point, infinite decimals

  // --------‑‑‑ Synchronization dependent on activeField ‑‑‑--------
  if (activeField.value === 'tomanPrice' && order.tomanPrice && Number(order.usd_tmn)) {
    // price = tomanPrice / usd_tmn   (with up to 8 decimal places to store)
    order.price = trimDecimals(
        Number(order.tomanPrice) / Number(order.usd_tmn),
        8
    )
  }

  if (activeField.value === 'price' && order.price && Number(order.usd_tmn)) {
    // tomanPrice = price * usd_tmn
    order.tomanPrice = (truncateTo2((Number(order.price) * Number(order.usd_tmn)))).toString()
  }

  recalc()
}

function onTomanPriceInput(raw) {
  const cleaned = sanitizeNumber(raw)
  if (cleaned === '') {
    order.tomanPrice = ''
    if (activeField.value === 'tomanPrice') activeField.value = null
  } else {
    order.tomanPrice = cleaned
    activeField.value = 'tomanPrice'
  }

  if (order.tomanPrice && Number(order.usd_tmn)) {
    order.price = trimDecimals(
        Number(order.tomanPrice) / Number(order.usd_tmn),
        8            // ← Maximum 8 decimal places
    )
  } else {
    order.price = ''
  }

  recalc()
}


/* ---------- Calculations ---------- */
function recalc() {
  const amt = Number(order.amount || 0)
  const prc = Number(order.price || 0)
  const usd = Number(order.usd_tmn || 0)
  const tomanPrice = Number(order.tomanPrice || 0)

  // Store the value without decimal places (numeric string)
  order.value = (amt && prc) ? (amt * prc).toString() : ''
  order.tomanValue = (amt && tomanPrice) ? (amt * tomanPrice).toString() : ''
}


/*
  * Only digits and a period are allowed if needed.
  * If `maxDecimals` is 0, periods are not allowed.
 */
function sanitizeNumber(raw) {
  let s = raw.replace(/,/g, '')           // Remove commas
  // Let there be only one dot and remove all other non-numeric characters.
  s = s.replace(/[^0-9.]/g, '')

  // Only one point should be kept.
  const parts = s.split('.')
  if (parts.length > 2) {
    s = parts[0] + '.' + parts.slice(1).join('')
  }

  // No limit on the number of digits after the decimal point.
  return s
}


/* ---------- Deactivating prices ---------- */
const priceDisabled = computed(() => activeField.value === 'tomanPrice')
const tomanPriceDisabled = computed(() => activeField.value === 'price')

/* ---------- Initial fetch ---------- */
async function fetchInitialData() {
  loading.value = true

  // 1) All GameAccounts
  await ordersStore.action_getGameAccounts()
  await getCurrencies(false)

  loading.value = false
}


const showStocksManagerBtn = computed(() =>
    selectedGame.value?.hasStocksManagerService === true
)

/* Object filter: Keep only keys that have the value truthy */
function buildPayload() {
  const payload = {}
  Object.entries(order).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) payload[k] = v
  })
  if (attachments.value.length) {
    payload.attachments = {paymentReceipts: attachments.value}
  }
  return payload
}

async function onSave() {
  // 1) Validation (if Vuelidate is available)
  $vulidate.value.$touch?.()
  if ($vulidate.value?.$invalid) {
    console.log($vulidate.value?.$invalid)
    selectedTab.value = 0
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  // 2) Filter filled fields
  const payload = buildPayload()
  if (Object.keys(payload).length === 0) {
    showMessage('No fields are filled in.', 'warning')
    return
  }

  // 3) User verification
  //const ok = await showAlert('You are adding new order?', 'Are you sure?', 'Yes', 'No')
  //if (!ok) return

  // 4) API call
  try {
    saving.value = true
    const res = await ordersStore.addOrder('CurrencyBuy', payload)
    if (res === true) {
      showMessage('New Order added!', 'success')
      backToOrders();
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }
}

async function onSaveAndSendToStock() {
  // 1) Validation (if Vuelidate is available)
  $vulidate.value.$touch?.()
  if ($vulidate.value?.$invalid) {
    selectedTab.value = 0
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  // 2) Filter filled fields
  const payload = buildPayload()
  if (Object.keys(payload).length === 0) {
    showMessage('No fields are filled in.', 'warning')
    return
  }

  const result = await Swal.fire({
    iconHtml: '<img src="' + warning + '">',
    title: "Send to Stock Manager",
    showCancelButton: true,
    confirmButtonText: 'Send to Stock Manager',
    cancelButtonText: '+1% Tax',
    customClass: {
      popup: 'bg-white dark:bg-slate-700 p-4 !flex flex-col gap-6 justify-center items-center',
      icon: 'm-0 border-0',
      actions: 'm-0 gap-1',
      title: '!p-0 !text-base dark:text-slate-300 !leading-8 font-iransansEnNum',
      htmlContainer: '!text-warning-500 !p-0',
      confirmButton: '!m-0 !bg-gray-800 rounded-l-xl rounded-r-none',
      cancelButton: '!m-0 !bg-gray-800 rounded-r-xl rounded-l-none'
    }
  })

  if (result.isConfirmed) {
    try {
      saving.value = true
      const res = await ordersStore.addCurrencyOrder('CurrencyBuy', payload)
      if (res.id && res.uuid) {
        order.id = res.id;
        order.uuid = res.uuid;
        showMessage('New Order added!', 'success')
        onSendToStocksManager()
      } else {
        showMessage(res, 'error')
      }
    } catch (err) {
      showMessage(err, 'error')
    } finally {
      saving.value = false
    }
  }
  if (result.isDismissed) {
    try {
      saving.value = true
      const res = await ordersStore.addCurrencyOrder('CurrencyBuy', payload)
      if (res.id && res.uuid) {
        order.id = res.id;
        order.uuid = res.uuid;
        showMessage('New Order added!', 'success')
        onSendToStocksManagerPlusTax()
      } else {
        showMessage(res, 'error')
      }
    } catch (err) {
      showMessage(err, 'error')
    } finally {
      saving.value = false
    }
  }
}


function onSendToStocksManager() {
  const isValid = $vulidate.value.$validate();

  if (!isValid) {
    return false;
  }

  plusTax.value = false;
  showTransfer.value = true;
  history.pushState({modal: true}, '')
}

function onSendToStocksManagerPlusTax() {
  const isValid = $vulidate.value.$validate();

  if (!isValid) {
    return false;
  }

  plusTax.value = true;
  showTransfer.value = true;
  history.pushState({modal: true}, '')
}

function closeModal(added) {
  showTransfer.value = false
  if (added) {
    router.push({name: 'currency-buy-orders'})
  } else {
    router.push({name: 'currency-buy-order-details', params: {id: order.uuid}})
    /*if (history.state?.modal) {
      history.back()
    }*/
  }
}

function backToOrders() {
  router.push({name: 'currency-buy-orders'})
}

function onPopState() {
  if (showTransfer.value) {
    showTransfer.value = false
    router.push({name: 'currency-buy-order-details', params: {id: order.uuid}})
  }
}

const getCurrencies = async (refresh) => {
  try {
    if (!refresh) fetching.value = true
    const res = await currencyPricesStore.action_getPrices();
    if (res.result === true) {
      const tomanIndex = res.data.findIndex(item => item.name === "Toman");

      const newItem = {
        ...res.data[tomanIndex],
        name: "Instant Toman"
      };

      currencyPrices.value = [
        ...res.data.slice(0, tomanIndex + 1),
        newItem,
        ...res.data.slice(tomanIndex + 1)
      ];

      selectedPaymentMethod.value = currencyPrices.value.find(item => item.name === 'USDT')
      tomanGoldCurrency.value = currencyPrices.value.find(item => item.name === 'Toman Gold Buy Auto')
      tomanJalaliCurrency.value = currencyPrices.value.find(item => item.name === 'Toman Gold Buy Manual')

      order.paymentMethod = selectedPaymentMethod.value.name
      order.usd_tmn = currencyPrices.value.find(item => item.name === 'Toman Gold Buy Auto').price
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 1500});
      showMessage(res, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    fetching.value = false
  }
}

const getSelectedTaker = (taker) => {
  selectedTaker.value = taker || null
  order.takerID = taker ? taker.id : null
}

const onChangePaymentMethod = (paymentMethod) => {
  order.paymentMethod = paymentMethod.name
  if (paymentMethod.name === 'USDT') {
    order.usd_tmn = tomanGoldCurrency.value.price
  } else if (paymentMethod.name === 'Toman' || paymentMethod.name === 'Instant Toman') {
    order.usd_tmn = tomanJalaliCurrency.value.price
  }
  onUsdInput(order.usd_tmn)
}

onMounted(() => {
  window.addEventListener('popstate', onPopState)
})

onBeforeUnmount(() => {
  window.removeEventListener('popstate', onPopState)
})

onMounted(fetchInitialData)

/* ---------- Watch for Game Change ---------- */
watch(
    () => order.game,
    () => onGameChange()
)


/* Related to hot orders */
const isShowAddHotOrderModal = ref(false)

function showAddHotOrderModal() {
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  isShowAddHotOrderModal.value = true
}

function closeAddHotOrderModal() {
  isShowAddHotOrderModal.value = false
}

function orderDetails(orderID) {
  const route = {name: 'currency-buy-order-details', params: {id: orderID}}
  router.replace(route)
}

async function saveOrderAndHotOrder(hotOrder) {
  // 1) Validation (if Vuelidate is available)
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    selectedTab.value = 0
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  // 2) Filter filled fields
  const payload = buildPayload()
  if (Object.keys(payload).length === 0) {
    showMessage('No fields are filled in.', 'warning')
    return
  }

  // 3) User verification
  //const ok = await showAlert('You are adding new order?', 'Are you sure?', 'Yes', 'No')
  //if (!ok) return

  // 4) API call
  try {
    saving.value = true
    const res = await ordersStore.addOrder2('CurrencyBuy', payload)
    if (res.result === true) {
      showMessage('New Order added!', 'success')
      try {
        saving.value = true
        const res2 = await ordersStore.action_addHotOrder_currencyBuy({...hotOrder, "orderID": res.orderID})
        if (res2 === true) {
          closeAddHotOrderModal();
          orderDetails(res.orderID);
        } else {
          showMessage(res2, 'error')
        }
      } catch (err) {
        showMessage(err, 'error')
      } finally {
        saving.value = false
      }
    } else {
      showMessage(res.error, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }

}
</script>

<template>
  <Transfer v-if="showTransfer" @close="closeModal" :order="order" :plusTax="plusTax"
            orderType="currencyBuy"></Transfer>
  <div v-else class="h-full">
    <TabGroup :selectedIndex="selectedTab" @change="(index)=> selectedTab = index">
      <div class="space-y-4 text-white/80 text-sm h-full">
        <!-- Loading mode -->
        <div v-if="loading" class="flex items-center justify-center h-60">
          <Icon icon="eos-icons:bubble-loading" class="size-12"/>
        </div>

        <!-- Order details form -->
        <TabPanels v-else class="h-full">
          <div class="flex flex-col gap-2 h-full">
            <div class="flex w-full">
              <div class="grid grid-cols-5 gap-4 h-full w-full">
                <div class="col-span-4 flex flex-row justify-between items-center">
                  <div class="flex flex-row gap-2 items-center">
                    <Tooltip placement="top" arrow>
                      <template #button>
                        <div class="back-btn" @click="backToOrders">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path fill="currentColor"
                                  d="m7.825 13l4.9 4.9q.3.3.288.7t-.313.7q-.3.275-.7.288t-.7-.288l-6.6-6.6q-.15-.15-.213-.325T4.426 12t.063-.375t.212-.325l6.6-6.6q.275-.275.688-.275t.712.275q.3.3.3.713t-.3.712L7.825 11H19q.425 0 .713.288T20 12t-.288.713T19 13z"/>
                          </svg>
                        </div>
                      </template>
                      <span>{{ $t('message.back') }}</span>
                    </Tooltip>
                    <TabList class="">
                      <Tab
                          v-slot="{ selected }"
                          as="template"
                          v-for="(item, i) in tabs"
                          :key="i"
                      >
                        <button
                            :class="[
                            selected
                              ? 'dark:text-white text-slate-100 bg-slate-600 dark:bg-primary-700'
                              : 'text-slate-700 bg-slate-300 dark:bg-slate-700 dark:text-slate-300',
                          ]"
                            class="text-sm font-medium last:mb-0 capitalize ring-0 foucs:ring-0 focus:outline-none px-6 py-2 transition duration-150 rounded-none first:rounded-l-2xl last:rounded-r-2xl  "
                        >
                          {{ item.title }}
                        </button>
                      </Tab>
                    </TabList>
                  </div>
                  <div class="flex flex-row items-center justify-end gap-2">
                    <div class="inline-block w-max relative" ref="stateDropdownRef">
                      <button @click="toggleStateDropdown"
                              :class="{ '!border !border-red-300': $vulidate.state.$error }"
                              class="w-full bg-slate-100 dark:bg-slate-900 border border-slate-700 text-slate-700 dark:text-slate-300 rounded-md px-2 py-2 text-left flex justify-between items-center flex-row gap-2">
                        <div class="flex items-center gap-2">
                          <span class="w-2 h-2 rounded-full" :class="stateColorsMap[order.state]"></span>
                          {{ order.state }}
                        </div>
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                      </button>
                      <div v-if="isStateOpen"
                           class="absolute right-0 overflow-hidden z-10 mt-1 w-max min-w-full bg-slate-100 dark:bg-slate-900 border border-slate-700 text-slate-700 dark:text-slate-300 rounded-md shadow-lg">
                        <ul>
                          <li v-for="state in states" :key="state" @click="selectState(state)"
                              class="px-2 py-2 hover:bg-slate-300 dark:hover:bg-slate-800 cursor-pointer flex items-center gap-2">
                            <span class="w-2 h-2 rounded-full" :class="stateColorsMap[state]"></span>
                            {{ state }}
                          </li>
                        </ul>
                      </div>
                      <template v-if="$vulidate.state.$error">
                        <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.state.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                  </div>
                </div>
                <div class="col-span-1">
                  <h6 class="text-center text-base py-2 bg-slate-100 dark:bg-slate-800 rounded-lg h-full">
                    Add Currency Buy Order
                  </h6>
                </div>
              </div>
            </div>
            <div class="flex-1 grid grid-cols-5 gap-4 h-full">
              <div class="col-span-4 flex flex-col relative h-full overflow-hidden">
                <hr class="border-0 h-0.5 bg-slate-800">
                <div class="flex flex-col gap-2 2xl:gap-4 relative h-full overflow-hidden">
                  <TabPanel :unmount="false"
                            class="col-span-4 flex-1 flex flex-col gap-4 relative h-full overflow-auto custom-scrollbar-2 pt-4">
                    <div class="flex flex-row items-start gap-4">
                      <!-- ================== Game ================== -->
                      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full md:w-max">
                        <FloatLabel class="w-full custom-select"
                                    :class="{ 'has-error': $vulidate.game.$error }"
                                    variant="on">
                          <Select v-model="order.game" inputId="game" :options="games" optionLabel="game"
                                  option-value="game" overlay-class="!z-[900]"
                                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                  :class="[$vulidate.game.$error ? '!border !border-red-300' : '!border-slate-700']"
                                  input-class="!text-xs 2xl:!text-sm"
                                  filter autoFilterFocus/>
                          <label for="game" class="!bg-slate-900">Game</label>
                        </FloatLabel>
                        <template v-if="$vulidate.game.$error">
                          <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.game.required.$response">
                            This field is required
                          </p>
                        </template>
                      </div>

                      <!-- ================== Server ================== -->
                      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel class="w-full custom-select"
                                    :class="{ 'has-error': $vulidate.server.$error }"
                                    variant="on">
                          <Select v-model="order.server" inputId="server" :options="servers" overlay-class="!z-[900]"
                                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                  :class="[$vulidate.server.$error ? '!border !border-red-300' : '!border-slate-700']"
                                  input-class="!text-xs 2xl:!text-sm"
                                  :virtualScrollerOptions="{ itemSize: 38 }"
                                  filter autoFilterFocus/>
                          <label for="server" class="!bg-slate-900">Server</label>
                        </FloatLabel>
                        <template v-if="$vulidate.server.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.server.required.$response">
                            This field is required
                          </p>
                        </template>
                      </div>

                      <!-- ================== Account ================== -->
                      <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-max">
                        <FloatLabel class="w-full custom-select"
                                    :class="{ 'has-error': $vulidate.account.$error }"
                                    variant="on">
                          <Select v-model="order.account" inputId="account" :options="accounts" optionLabel="title"
                                  option-value="id" overlay-class="!z-[900]"
                                  class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                  :class="[$vulidate.account.$error ? '!border !border-red-300' : '!border-slate-700']"
                                  input-class="!text-xs 2xl:!text-sm"
                                  :virtualScrollerOptions="{ itemSize: 32 }"
                                  filter autoFilterFocus/>
                          <label for="account" class="!bg-slate-900">Account</label>
                        </FloatLabel>
                        <template v-if="$vulidate.account.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.account.required.$response">
                            This field is required
                          </p>
                        </template>
                      </div>
                    </div>
                    <div class="grid grid-cols-5 gap-4">
                      <!-- ================== Supplier ================== -->
                      <!--                      <div class="col-span-5 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                                              <FloatLabel variant="on">
                                                <InputText id="supplier" v-model="order.supplier" autocomplete="off"
                                                           class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                                           :class="{ 'has-error': $vulidate.supplier.$error }"/>
                                                <label for="supplier" class="!bg-slate-900"
                                                       :class="{ 'focus:text-red-300': $vulidate.supplier.$error }">Supplier</label>
                                              </FloatLabel>
                                              <template v-if="$vulidate.supplier.$error">
                                                <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.supplier.required.$response">
                                                  This field is required
                                                </p>
                                              </template>
                                            </div>-->

                      <!-- ================== Supplier ================== -->
                      <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <Users v-model="selectedTaker" @update:modelValue="getSelectedTaker" appendTo="body"
                               label="Supplier"
                               :required="true"/>
                      </div>

                      <!-- ================== Payment Method ================== -->
                      <div class="col-span-1 flex flex-row gap-2 text-xs 2xl:text-sm">
                        <div class="flex-1 w-full">
                          <FloatLabel variant="on" :class="{ 'has-error': $vulidate.paymentMethod.$error }">
                            <Select v-model="selectedPaymentMethod" input-id="paymentMethod"
                                    :options="currencyPrices.filter(item => includeCurrencyPrices.includes(item.name))"
                                    optionLabel="name" :loading="fetching"
                                    class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                    :class="{ 'has-error has-addon': $vulidate.paymentMethod.$error }"
                                    label-class="!text-sm !items-center !flex"
                                    @update:modelValue="onChangePaymentMethod">
                              <template #option="slotProps">
                                <div class="flex flex-row items-center gap-4 justify-between w-full">
                                  <span class="text-sm">{{ slotProps.option.name }}</span>
                                </div>
                              </template>
                            </Select>
                            <label for="paymentMethod" class="!bg-slate-900"
                                   :class="{ 'has-error': $vulidate.paymentMethod.$error }">Payment Method</label>
                          </FloatLabel>
                        </div>
                        <button @click="getCurrencies(false)">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                               class="lucide lucide-refresh-cw-icon lucide-refresh-cw">
                            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                            <path d="M21 3v5h-5"/>
                            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                            <path d="M8 16H3v5"/>
                          </svg>
                        </button>
                      </div>

                      <div v-if="selectedTaker?.id"
                           class="col-span-5 flex flex-col w-full gap-2 border border-slate-700 rounded-md p-2">
                        <div class="flex flex-row gap-2 w-full items-center">
                          <div @click.stop="showUserProfileModal(selectedTaker)" class="cursor-zoom-in">
                            <img v-if="selectedTaker.discordAvatar" :src="selectedTaker.discordAvatar"
                                 alt="avatar" class="size-10 rounded-full border border-slate-700">
                            <div v-else
                                 class="flex items-center justify-center size-10 rounded-full border border-slate-700">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                   fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                   stroke-linejoin="round" class="lucide lucide-user-icon lucide-user">
                                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                              </svg>
                            </div>
                          </div>
                          <div class="flex-1 grid grid-cols-2 gap-1">
                            <div class="flex flex-col">
                              <div class="flex flex-row gap-2 items-center">
                                <div>{{ selectedTaker.name }} &nbsp &nbsp</div>
                              </div>
                              <div class="flex flex-row w-max text-xs text-slate-400">
                                <div>{{ selectedTaker.discordUsername ?? "-" }}</div>
                                <div>{{ "&nbsp- " + selectedTaker.discordDisplayName ?? "-" }}</div>
                              </div>
                            </div>
                            <div class="flex flex-col">
                              <div>{{ selectedTaker.verificationState ?? "-" }}</div>
                              <div>{{ discordRegistrationTime(selectedTaker.discordID) }}</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- ================== Amount ================== -->
                      <div class="col-span-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on">
                          <InputText id="amount" v-model="order.amount" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error': $vulidate.amount.$error }"
                                     @keydown="allowDigitsDot"
                                     @input="onAmountInput($event.target.value)"/>
                          <label for="amount" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.amount.$error }">Amount</label>
                        </FloatLabel>
                        <template v-if="$vulidate.amount.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.amount.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.amount.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>

                      <!-- ================== Price per unit (USD) ================== -->
                      <div class="col-span-2 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on"
                                    :class="{ 'has-error': $vulidate.price.$error }">
                          <InputText id="price" v-model="order.price" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error has-addon': $vulidate.price.$error }"
                                     :disabled="priceDisabled"
                                     @keydown="allowDigitsDot"
                                     @input="onPriceInput($event.target.value)"/>
                          <label for="price" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.price.$error }">Price/Unit</label>
                        </FloatLabel>
                        <template v-if="$vulidate.price.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.price.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.price.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>

                      <!-- ================== Value (USD) ================== -->
                      <div class="col-span-2 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on">
                            <InputText id="value" v-model="order.value" autocomplete="off"
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                       disabled/>
                            <label for="value" class="!bg-slate-900">Value</label>
                          </FloatLabel>
                          <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px]">$
                          </InputGroupAddon>
                        </InputGroup>
                      </div>

                      <!-- ================== USD/TMN ================== -->
                      <div class="col-span-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on"
                                    :class="{ 'has-error': $vulidate.usd_tmn.$error }">
                          <InputText id="usd_tmn" v-model="order.usd_tmn" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error has-addon': $vulidate.usd_tmn.$error }"
                                     @keydown="allowDigitsDot"
                                     @input="onUsdInput($event.target.value)"/>
                          <label for="usd_tmn" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.usd_tmn.$error }">USD/TMN</label>
                        </FloatLabel>
                        <template v-if="$vulidate.usd_tmn.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.usd_tmn.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.usd_tmn.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>
                      <!-- ================== Toman Price per unit ================== -->
                      <div class="col-span-2 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on"
                                    :class="{ 'has-error': $vulidate.tomanPrice.$error }">
                          <InputText id="tPrice" v-model="order.tomanPrice" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error has-addon': $vulidate.tomanPrice.$error }"
                                     :disabled="tomanPriceDisabled"
                                     @keydown="allowDigitsDot"
                                     @input="onTomanPriceInput($event.target.value)"/>
                          <label for="tPrice" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.tomanPrice.$error }">Toman Price/Unit</label>
                        </FloatLabel>
                        <template v-if="$vulidate.tomanPrice.$error">
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.tomanPrice.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs"
                             v-if="!$vulidate.tomanPrice.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>

                      <!-- ================== Toman Value ================== -->
                      <div class="col-span-2 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on">
                            <InputText id="tValue" v-model="order.tomanValue" :value="formatNumber(order.tomanValue, 0)"
                                       autocomplete="off"
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                       disabled/>
                            <label for="tValue" class="!bg-slate-900">Toman Value</label>
                          </FloatLabel>
                          <InputGroupAddon class="!bg-transparent !border-slate-700 h-[32px] 2xl:h-[40px]">Tmn
                          </InputGroupAddon>
                        </InputGroup>
                      </div>
                    </div>
                    <!--                    <HotOrders @showAddHotOrderModal="showAddHotOrderModal"/>-->
                  </TabPanel>
                  <TabPanel :unmount="false"
                            class="flex-1 flex flex-col gap-6 relative h-full overflow-auto px-2 custom-scrollbar-2 pt-4">
                    <!-- Only changes -->
                    <Attachments
                        id="addOrder"
                        :count="16"
                        :only-changes="true"
                        v-model:uploading="isUploadingAttachment"
                        @update:payload="val => (attachments = val)"
                    />
                  </TabPanel>
                </div>
              </div>
              <div class="col-span-1 flex flex-col h-full overflow-hidden">
                <hr class="border-0 h-0.5 bg-slate-800">
                <div class="flex flex-col h-full overflow-hidden gap-4 pt-4">
                  <div class="flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm h-full">
                    <FloatLabel variant="on" class="h-full">
                      <Textarea id="notes" v-model="order.notes" rows="5" style="resize: none"
                                class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 h-full"/>
                      <label for="notes" class="!bg-slate-900">Notes</label>
                    </FloatLabel>
                  </div>
                  <hr class="border-0 h-0.5 bg-slate-800">
                  <div class="grid gap-2">
                    <template
                        v-if="userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('STOCKS_MANAGER_ACCESS')">
                      <button v-if="showStocksManagerBtn" @click="onSaveAndSendToStock"
                              class="btn btn-outline-primary py-2"
                              :disabled="saving">
                    <span v-if="saving"
                          class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0">
                      <Loading/>
                    </span>
                        Save And Send to Stock Manager
                      </button>
                    </template>
                    <button @click="onSave" class="btn btn-outline-primary py-2" :disabled="saving">
                  <span v-if="saving"
                        class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0">
                    <Loading/>
                  </span>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanels>
      </div>
    </TabGroup>
    <AddHotOrder v-if="isShowAddHotOrderModal" @closeModal="closeAddHotOrderModal"
                 @saveHotOrder="saveOrderAndHotOrder" :saving="saving"/>
  </div>
</template>

<style>
@import "@/assets/primevue.css";
</style>
<style scoped>
.input {
  @apply w-full px-3 py-2 border border-gray-700 rounded-md focus:outline-none focus:ring focus:border-blue-500;
}

.back-btn {
  @apply h-6 2xl:h-8 w-6 2xl:w-8 bg-slate-200 dark:bg-slate-700 dark:text-slate-200 text-slate-600 flex flex-col justify-center items-center text-base rounded-full cursor-pointer;
}

:deep(.p-floatlabel:has(input.has-error) label) {
  --p-floatlabel-focus-color: rgb(252, 165, 165);
}

:deep(.p-floatlabel:has(input.has-error)) {
  @apply !border !border-red-300 !rounded-md
}

:deep(.p-inputgroup .p-floatlabel.has-error) {
  @apply !border-r-0 !rounded-r-none
}

:deep(.p-inputgroup:has(.p-floatlabel.has-error) .p-inputgroupaddon) {
  @apply !border !border-red-300 !rounded-md !border-l-0 !rounded-l-none
}


:deep(.p-select-clear-icon) {
  @apply size-3
}

:deep(.p-select-dropdown) {
  @apply w-6
}


/*------------*/
/*:deep(.custom-select.p-floatlabel label) {
  top: 0 !important;
  transform: translateY(-50%);
  @apply bg-slate-900 py-0 px-[0.125rem];
}*/

/*:deep(.p-floatlabel.custom-select label) {
  font-size: 0.75rem
}*/

:deep(.p-floatlabel.custom-select.has-error label) {
  /*--p-floatlabel-color: rgb(252, 165, 165);*/
  --p-floatlabel-focus-color: rgb(252, 165, 165);
}


/*:root {
  --p-select-option-focus-background: #521111;
  --p-select-option-focus-color: #707007;
}*/
</style>